import {
  Button,
  ButtonIcon,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "@platform/ui/components";
import { Link } from "react-router";
import { EditSavedExternalRoleDialog, EditSavedInternalRoleDialog } from "~/participant/components/EditSavedRoleDialog";
import { EditSavedRoleNotesDialog } from "~/participant/components/EditSavedRoleNotesDialog";
import { COMPANIES_PATHS } from "~/participant/constants/paths.constants";
import { useRemoveSavedRoleById } from "~/participant/hooks/useRemoveSavedRoleById";
import { ExternalSavedRole, InternalSavedRole } from "~/shared/api/participants/participants.types";
import { toast } from "~/shared/components/ds/Toast/Toast";

export function SavedRolesRowActions({ row }: { row: InternalSavedRole | ExternalSavedRole }) {
  const { id, type, notes } = row;

  switch (type) {
    case "internal": {
      return (
        <SavedRolesActionsLayout>
          {row.job.url && row.job?.url?.length > 0 && (
            <DropdownMenuItem asChild>
              <Link
                className="no-underline focus-visible:ring-0"
                to={row.job.url}
                target="_blank"
                rel="noreferrer noopener"
              >
                <Icon className="size-4" aria-hidden symbol="external-link-line" />
                View
              </Link>
            </DropdownMenuItem>
          )}
          {row.company?.id && (
            <DropdownMenuItem asChild>
              <Link className="no-underline focus-visible:ring-0" to={COMPANIES_PATHS.detail(row.company.id.toFixed())}>
                <Icon className="size-4" aria-hidden symbol="building-line" />
                Company
              </Link>
            </DropdownMenuItem>
          )}
          <EditSavedRoleNotesDialog notes={notes ?? ""} savedRoleId={id}>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
              }}
            >
              <Icon className="size-4" aria-hidden symbol="file-text-line" />
              Notes
            </DropdownMenuItem>
          </EditSavedRoleNotesDialog>
          <EditSavedInternalRoleDialog savedRoleId={id}>
            <DropdownMenuItem
              onSelect={(event) => {
                // Prevent the dropdown from closing when the delete button is clicked.
                event.preventDefault();
              }}
            >
              <Icon className="size-4" aria-hidden symbol="edit-line" />
              Edit
            </DropdownMenuItem>
          </EditSavedInternalRoleDialog>
          <RemoveSavedRoleItem savedRoleId={id} />
        </SavedRolesActionsLayout>
      );
    }
    case "external": {
      return (
        <SavedRolesActionsLayout>
          <EditSavedRoleNotesDialog notes={notes ?? ""} savedRoleId={id}>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
              }}
            >
              <Icon className="size-4" aria-hidden symbol="file-text-line" />
              Notes
            </DropdownMenuItem>
          </EditSavedRoleNotesDialog>

          <EditSavedExternalRoleDialog savedRoleId={id}>
            <DropdownMenuItem
              onSelect={(event) => {
                // Prevent the dropdown from closing when the delete button is clicked.
                event.preventDefault();
              }}
            >
              <Icon className="size-4" aria-hidden symbol="edit-line" />
              Edit
            </DropdownMenuItem>
          </EditSavedExternalRoleDialog>
          <RemoveSavedRoleItem savedRoleId={id} />
        </SavedRolesActionsLayout>
      );
    }

    default: {
      return null;
    }
  }
}

function SavedRolesActionsLayout({ children }: { children?: React.ReactNode }) {
  return (
    <DropdownMenu>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button className="ml-auto" svgOnly variant="ghost">
                <ButtonIcon symbol="ri-more-fill" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent side="bottom">Actions</TooltipContent>
          </TooltipPortal>
        </Tooltip>
      </TooltipProvider>

      <DropdownMenuContent align="end">
        <DropdownMenuGroup>
          <DropdownMenuLabel className="sr-only">List of actions for saved role</DropdownMenuLabel>
          {children}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function RemoveSavedRoleItem({ savedRoleId }: { savedRoleId: number }) {
  const mutation = useRemoveSavedRoleById({
    savedRoleId,
  });

  return (
    <DropdownMenuItem
      variant="destructive"
      disabled={mutation.isPending}
      onClick={() => {
        toast.promise(mutation.mutateAsync(), {
          loading: "Removing saved role...",
          success: "Successfully removed role",
          error: "Failed to remove role",
        });
      }}
    >
      <Icon className="size-4" aria-hidden symbol="delete-bin" />
      Remove
    </DropdownMenuItem>
  );
}
